import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { CountryApiRow, CurrencyApiRow } from '@/api/reference-admin';
import { getCountries, getCurrencies } from '@/api/reference-admin';
import { isInternalAdminUser } from '@/lib/authz';
import { useKeycloakAuth } from './keycloak-auth-context';

export interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownOptionsContextType {
  currencyOptions: DropdownOption[];
  countryOptions: DropdownOption[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

const DropdownOptionsContext = createContext<DropdownOptionsContextType | undefined>(undefined);

interface DropdownOptionsProviderProps {
  children: React.ReactNode;
}

export const DropdownOptionsProvider: React.FC<DropdownOptionsProviderProps> = ({ children }) => {
  const [currencyOptions, setCurrencyOptions] = useState<DropdownOption[]>([]);
  const [countryOptions, setCountryOptions] = useState<DropdownOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, isLoading: authLoading, user } = useKeycloakAuth();

  const fetchOptions = useCallback(async () => {
    // Only proceed if authentication is complete and user is authorized
    if (authLoading) {
      return; // Wait for authentication to complete
    }

    if (!isAuthenticated) {
      setError('Authentication required');
      return;
    }

    // Reference data (currencies/countries) is admin-tier; only internal admins consume
    // these dropdowns (reference-admin modals), so avoid spurious 403s for internal-basic.
    if (!isInternalAdminUser(user?.roles)) {
      setError('Internal admin access required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch currencies and countries in parallel (use large pageSize for dropdown use case)
      const [currenciesResponse, countriesResponse] = await Promise.all([
        getCurrencies({ pageSize: 500 }), // Get all currencies for dropdown
        getCountries({ pageSize: 500 }), // Get all countries for dropdown
      ]);

      // Transform currencies to dropdown options (filter for active only)
      const currencies: DropdownOption[] = currenciesResponse.data
        .filter((currency: CurrencyApiRow) => currency.is_active) // Filter active currencies
        .map((currency: CurrencyApiRow) => ({
          value: currency.code,
          label: `${currency.code} - ${currency.name}`,
        }))
        .sort((a, b) => a.value.localeCompare(b.value));

      // Transform countries to dropdown options (filter for active only)
      const countries: DropdownOption[] = countriesResponse.data
        .filter((country: CountryApiRow) => country.is_active) // Filter active countries
        .map((country: CountryApiRow) => ({
          value: country.code,
          label: `${country.code} - ${country.name}`,
        }))
        .sort((a, b) => a.value.localeCompare(b.value));

      setCurrencyOptions(currencies);
      setCountryOptions(countries);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dropdown options');
      // Note: Error is already captured in state for UI display
    } finally {
      setIsLoading(false);
    }
  }, [authLoading, isAuthenticated, user?.roles]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]); // Dependencies: fetchOptions already includes auth state dependencies

  const value: DropdownOptionsContextType = {
    currencyOptions,
    countryOptions,
    isLoading,
    error,
    refetch: fetchOptions,
  };

  return (
    <DropdownOptionsContext.Provider value={value}>{children}</DropdownOptionsContext.Provider>
  );
};

export const useDropdownOptions = (): DropdownOptionsContextType => {
  const context = useContext(DropdownOptionsContext);
  if (context === undefined) {
    throw new Error('useDropdownOptions must be used within a DropdownOptionsProvider');
  }
  return context;
};
